import { Button, Chip, Typography } from "@mui/material";
import supabase from "../../../../../utils/supabase";
import { selectInBatched } from "../../../../../utils/supabase-batch";

const TAG = "[scientificNames.plugin]";

/* ========== helpers ========== */
const norm = (s) =>
  (s ?? "")
    .toString()
    .normalize("NFKC")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const pickFirst = (obj, keys = []) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

// Author 表示: 2名なら「A & B」、3名以上は「A, B & C」
const formatAuthors = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "—";
  const names = [...rows]
    .sort((a, b) => (a?.author_order ?? 0) - (b?.author_order ?? 0))
    .map((r) => r?.researchers)
    .filter(Boolean)
    .map((a) => a?.last_name || a?.lastName)
    .filter(Boolean);

  if (names.length === 0) return "—";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ja-JP", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

/* ========== plugin ========== */
const plugin = {
  table: "scientific_names",
  defaultSort: { field: "name_spell_valid", sort: "asc" },

  // *FK に頼らず* 必要なものは自前で合成できるよう、最低限の列を取得
  select: `
    id,
    name_spell_valid,
    name_spell_original,
    authority_year,
    current_rank,
    current_parent,
    original_rank,
    original_parent,
    valid_name_id,
    type_taxa_id,
    extant_fossil,
    type_category,
    type_locality,
    type_sex,
    type_repository,
    type_repository_id,
    type_host,
    source_of_original_description,
    page,
    remark,
    last_update,
    scientific_name_and_author (
      author_order,
      researchers:researcher_id (
        id,
        last_name,
        first_name
      )
    )
  `,

  normalize(row) {
    return {
      ...row,
      _authors: formatAuthors(row?.scientific_name_and_author),
      _lastUpdateDisp: fmtDate(row?.last_update),
    };
  },

  /**
   * サーバーサイドページング版 fetch
   */
  async fetch({ page, pageSize, orderBy, ascending, search }) {
    const from = page * pageSize;
    const to = from + pageSize - 1;

    const orderMap = {
      id: "id",
      name_spell_valid: "name_spell_valid",
      name_spell_original: "name_spell_original",
      authority: "authority_year", // 代理
      authority_year: "authority_year",
      current_rank: "current_rank",
      current_parent: "current_parent",
      original_rank: "original_rank",
      original_parent: "original_parent",
      valid_name_id: "valid_name_id",
      type_taxa_id: "type_taxa_id",
      extant_fossil: "extant_fossil",
      type_category: "type_category",
      type_locality: "type_locality",
      type_sex: "type_sex",
      type_repository: "type_repository",
      type_host: "type_host",
      source_of_original_description: "source_of_original_description",
      page: "page",
      remark: "remark",
      last_update: "last_update",
      status: "valid_name_id",
    };
    const orderColumn = orderMap[orderBy] || "name_spell_valid";

    const likeOr = () => {
      if (!search) return undefined;
      const like = `%${search}%`;
      return [
        `id.ilike.${like}`,
        `name_spell_valid.ilike.${like}`,
        `name_spell_original.ilike.${like}`,
        `current_parent.ilike.${like}`,
        `original_parent.ilike.${like}`,
        `type_host.ilike.${like}`,
        `remark.ilike.${like}`,
      ].join(",");
    };

    const selectStr = `
      id,
      name_spell_valid,
      name_spell_original,
      authority_year,
      current_rank,
      current_parent,
      original_rank,
      original_parent,
      valid_name_id,
      type_taxa_id,
      extant_fossil,
      type_category,
      type_locality,
      type_sex,
      type_repository,
      type_repository_id,
      type_host,
      source_of_original_description,
      page,
      remark,
      last_update,
      scientific_name_and_author (
        author_order,
        researchers:researcher_id ( id, last_name, first_name )
      )
    `;

    let query = supabase.from(this.table).select(selectStr, { count: "exact" }).range(from, to);
    const orExpr = likeOr();
    if (orExpr) query = query.or(orExpr);
    try {
      query = query.order(orderColumn, { ascending });
    } catch {
      query = query.order("name_spell_valid", { ascending: true });
    }

    const { data, error, count } = await query;
    if (error) {
      console.error(`${TAG} fetch error`, error);
      return { rows: [], total: 0 };
    }
    const rows = Array.isArray(data) ? data : [];

    // valid_name 解決
    const validIds = Array.from(new Set(rows.map((r) => r.valid_name_id).filter(Boolean)));
    const id2validName = new Map();
    if (validIds.length) {
      try {
        const valRows = await selectInBatched({
          table: "scientific_names",
          column: "id",
          values: validIds,
          select: "id, name_spell_valid",
          chunkSize: 200,
        });
        (valRows || []).forEach((r) => id2validName.set(r.id, r.name_spell_valid || ""));
      } catch (e) {
        console.warn(`${TAG} valid name fetch failed`, e);
      }
    }

    // Repository 解決（type_repository が空かつ UUID があるときのみ）
    const needRepoIds = Array.from(
      new Set(
        rows
          .filter((r) => !r.type_repository && r.type_repository_id)
          .map((r) => r.type_repository_id)
      )
    );
    const repoMap = new Map();
    if (needRepoIds.length) {
      try {
        const repoRows = await selectInBatched({
          table: "Repositories",
          column: "uuid",
          values: needRepoIds,
          select: "uuid, acronym, name_en, city, country",
          chunkSize: 200,
        });
        (repoRows || []).forEach((r) => {
          const parts = [r?.acronym, r?.name_en].filter(Boolean).join(" — ");
          const loc = [r?.city, r?.country].filter(Boolean).join(", ");
          repoMap.set(r.uuid, loc ? `${parts} (${loc})` : parts);
        });
      } catch (e) {
        console.warn(`${TAG} repo fetch failed`, e);
      }
    }

    const synonymKeywords = ["synonym", "junior synonym", "objective synonym", "subjective synonym"];
    const mapped = rows.map((r) => {
      const _nameValidDisp = pickFirst(r, ["name_spell_valid", "valid_name", "name_valid", "name"]);
      const _nameOrigDisp = pickFirst(r, ["name_spell_original", "name_original", "original_name"]);

      const rankLower = (r.current_rank || "").toString().toLowerCase();
      const isSynonymByRank = synonymKeywords.some((kw) => rankLower === kw);
      const isSynonymByLink = !!(r.valid_name_id && r.valid_name_id !== r.id);
      const _is_synonym = isSynonymByRank || isSynonymByLink;

      const _validName = isSynonymByLink ? id2validName.get(r.valid_name_id) || "" : "";
      const _typeRepo =
        r.type_repository ||
        (r.type_repository_id ? repoMap.get(r.type_repository_id) || r.type_repository_id : "");

      return this.normalize({
        ...r,
        _nameValidDisp,
        _nameOrigDisp,
        _is_synonym,
        _validName,
        _typeRepo,
        _remark: r?.remark ?? r?.Remark ?? "",
      });
    });

    return { rows: mapped, total: typeof count === "number" ? count : mapped.length };
  },

  columns({ onEdit }) {
    return [
      {
        field: "__actions",
        headerName: "Actions",
        width: 96,
        sortable: false,
        filterable: false,
        renderCell: (p) =>
          p?.row ? (
            <Button size="small" variant="outlined" onClick={() => onEdit?.(p.row)}>
              Edit
            </Button>
          ) : null,
      },
      {
        field: "id",
        headerName: "ID",
        width: 220,
        renderCell: (p) => <span style={{ fontWeight: 600 }}>{p?.row?.id || ""}</span>,
      },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        sortable: false,
        renderCell: (p) => {
          const syn = !!p?.row?._is_synonym;
          return <Chip size="small" color={syn ? "warning" : "success"} label={syn ? "Synonym" : "Valid Name"} />;
        },
      },
      {
        field: "valid_name_id",
        headerName: "Valid Name",
        minWidth: 220,
        flex: 1,
        renderCell: (p) =>
          !p?.row?._is_synonym ? (
            <Typography variant="body2" color="text.secondary">—</Typography>
          ) : (
            <Typography variant="body2">{p?.row?._validName || "—"}</Typography>
          ),
      },
      {
        field: "name_spell_valid",
        headerName: "Name (valid)",
        minWidth: 220,
        flex: 1,
        renderCell: (p) => <Typography variant="body2">{p?.row?._nameValidDisp || "—"}</Typography>,
      },
      {
        field: "name_spell_original",
        headerName: "Original Spelling",
        minWidth: 200,
        flex: 1,
        renderCell: (p) => <Typography variant="body2">{p?.row?._nameOrigDisp || "—"}</Typography>,
      },
      {
        field: "authority",
        headerName: "Authority",
        minWidth: 200,
        renderCell: (p) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {p?.row?._authors || "—"}
          </Typography>
        ),
        sortable: false,
      },
      { field: "authority_year", headerName: "Year", width: 90 },
      { field: "current_rank", headerName: "Current Rank", width: 120 },
      { field: "current_parent", headerName: "Current Parent", width: 160 },
      { field: "original_rank", headerName: "Original Rank", width: 120 },
      { field: "original_parent", headerName: "Original Parent", width: 160 },
      { field: "type_taxa_id", headerName: "Type Taxa ID", width: 150 },
      { field: "extant_fossil", headerName: "Extant/Fossil", width: 120 },
      { field: "type_category", headerName: "Type Category", width: 140 },
      { field: "type_locality", headerName: "Type Locality", width: 160 },
      { field: "type_sex", headerName: "Type Sex", width: 100 },
      {
        field: "type_repository",
        headerName: "Type Repository",
        minWidth: 240,
        flex: 1,
        renderCell: (p) => <Typography variant="body2">{p?.row?._typeRepo || "—"}</Typography>,
      },
      { field: "type_host", headerName: "Type Host", width: 160 },
      { field: "source_of_original_description", headerName: "Source", width: 180 },
      { field: "page", headerName: "Page", width: 80 },
      {
        field: "remark",
        headerName: "Remark",
        minWidth: 220,
        flex: 1,
        renderCell: (p) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
            {p?.row?._remark || "—"}
          </Typography>
        ),
      },
      {
        field: "last_update",
        headerName: "Last Update",
        width: 160,
        renderCell: (p) => <Typography variant="body2">{p?.row?._lastUpdateDisp || ""}</Typography>,
      },
    ];
  },
};

export default plugin;
